// Put all packages together.
// Used to generate umd/index.prod.js

import React from 'react';
import { createRoot } from 'react-dom/client';

import { IntlProvider } from 'react-intl';

import TinodeWeb from './views/tinode-web.jsx';
import HashNavigation from './lib/navigation.js';
import LocalStorageUtil from './lib/local-storage.js';

// NOTE (Nexus hardening): the upstream Google Analytics (gtag.js) loader was
// REMOVED here — a certified, private messenger must not phone home to Google.
// No web telemetry is loaded. If self-hosted analytics is ever wanted, use a
// self-hosted stack (e.g. Matomo/Plausible), never a third-party tracker.

// Allow loading translation strings for just one language.
// Nexus offers three UI languages: Uzbek (Cyrillic, default), Uzbek (Latin) and
// Russian. English is kept as the technical base/fallback for any untranslated key.
const messageLoader = {
  'en': _ => import('./i18n.min/en.json'),
  'ru': _ => import('./i18n.min/ru.json'),
  'uz-Cyrl': _ => import('./i18n.min/uz-Cyrl.json'),
  'uz-Latn': _ => import('./i18n.min/uz-Latn.json')
};

// The default UI language for users who have not chosen one.
const DEFAULT_LANGUAGE = 'uz-Cyrl';

// List of languages that are written right-to-left.
const rtl = ['ar', 'fa', 'he', 'ur'];

// UI language selection. The user picks it in Settings (persisted in localStorage);
// new users default to Uzbek Cyrillic. Order: saved choice -> URL hash #?hl= -> default.
const { params } = HashNavigation.parseUrlHash(window.location.hash);
const language = LocalStorageUtil.getObject('ui-language') ||
  (params && params.hl) ||
  DEFAULT_LANGUAGE;

// Make sure names like 'en_GB' and 'en-GB' consistently use '-'.
const normalized = language.replace('_', '-');
// Get the base language 'en' from a more specific 'en-US' as a partial fallback.
const baseLanguage = normalized.split('-')[0].toLowerCase();

// Try the full locale first, then the locale without the region code, fallback to 'en'.
const htmlLang = messageLoader[normalized] ? language : messageLoader[baseLanguage] ? baseLanguage : 'en';

const html = document.getElementsByTagName('html')[0];
// Set lang attribute of the HTML element: <html lang="XX">
html.setAttribute('lang', htmlLang);
if (rtl.includes(baseLanguage)) {
  // Set right-to-left dir attribute of the HTML element: <html dir="rtl">
  html.setAttribute('dir', 'rtl');
}
// Render the app.
const root = createRoot(document.getElementById('mountPoint'));
messageLoader[htmlLang]().then(messages =>
  root.render(
    React.createElement(IntlProvider,
      { locale: language, messages: messages, textComponent: React.Fragment },
      React.createElement(TinodeWeb, null)
    )
));
